import type { PartnerStructuredColumnDto } from '@artic/api-client';
import { Button } from '@artic/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@artic/ui/dialog';
import { FileSelection } from '@artic/ui/file-selection';
import { toast } from '@artic/ui/toast';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { customColumnDefinitionsQueryOptions } from '../../custom-columns/queries';
import { ImportMappingEditor } from '../../imports/import-mapping-editor';
import { getMissingRequiredMappingFields } from '../../imports/mapping-warnings';
import { toImportProducts } from '../../imports/product-resolution';
import { latestMappingQueryOptions, useCreateImport } from '../../imports/queries';
import {
  PRODUCT_CODE_TARGET,
  PRODUCT_NAME_TARGET,
  PROJECT_CODE_TARGET,
  PROJECT_NAME_TARGET,
  WORK_ORDER_STATUS_TARGET,
  type ImportMapping,
} from '../../imports/transform';
import { partnerContainersQueryOptions, productListQueryOptions } from '../../products/queries';
import type { PartnerContainer } from '../../products/types';
import { partnerStructuredColumnsQueryOptions } from '../../structured-columns/queries';
import type { ProjectStatus, WorkOrderRow } from '../types';
import { buildImportCatalog, type ImportTarget } from './import-catalog';
import { validateImport } from './import-validation';
import { parseWorkbook, type ParsedWorkbook } from './parse-workbook';
import { PreviewStep } from './preview-step';

type Step = 'select' | 'map' | 'preview';

/**
 * When importing from within a project the target project and created status are
 * fixed, so these three rows render disabled in the mapping editor.
 */
const LOCKED_PROJECT_IMPORT_FIELDS: ReadonlySet<string> = new Set([
  WORK_ORDER_STATUS_TARGET,
  PROJECT_CODE_TARGET,
  PROJECT_NAME_TARGET,
]);

/**
 * Importing without a project (from the Projects index) fixes only the created
 * status; the project comes from the sheet, exactly as it does for an Automatic
 * (Drive) import.
 */
const LOCKED_PARTNER_IMPORT_FIELDS: ReadonlySet<string> = new Set([WORK_ORDER_STATUS_TARGET]);

/** Project Code is the key both project matching and auto-create work from. */
const PARTNER_IMPORT_REQUIRED_FIELDS: ReadonlySet<string> = new Set([PROJECT_CODE_TARGET]);

/** Stable empties so the validation memo isn't invalidated every render. */
const EMPTY_LEGACY_IDS: ReadonlySet<string> = new Set<string>();
const EMPTY_WORK_ORDERS: WorkOrderRow[] = [];

/** The project an import is pinned to, when it is launched from inside one. */
export interface ExcelImportProjectContext {
  projectId: string;
  /** Pinned into the mapping as locked static values. */
  projectCode: string;
  projectName: string;
  projectStatus: ProjectStatus;
  /** Existing rows the preview matches against, for update-vs-create. */
  workOrders: WorkOrderRow[];
  legacyWorkOrderIds: ReadonlySet<string>;
}

export interface ExcelImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partnerId: string;
  /**
   * Omit to import at partner level (the Projects index): rows carry their own
   * Project Code, which becomes a required mapping, and the server matches or
   * creates each project per row — the Automatic (Drive) import's behaviour.
   */
  project?: ExcelImportProjectContext;
  /**
   * Partner configuration. Supplied by the project page, which already holds
   * it; omitted at partner level, where the dialog fetches it on open so the
   * Projects index pays nothing until someone imports.
   */
  containers?: PartnerContainer[];
  structuredColumns?: PartnerStructuredColumnDto[];
}

export function ExcelImportDialog(props: ExcelImportDialogProps) {
  const { open, onOpenChange, project } = props;
  const navigate = useNavigate();

  const { data: metadataDefinitions = [] } = useQuery(
    customColumnDefinitionsQueryOptions(props.partnerId),
  );

  // Partner-level imports have no project page to inherit configuration from.
  // `enabled` keeps these inert whenever the caller passed the data in.
  const { data: fetchedContainers } = useQuery({
    ...partnerContainersQueryOptions(props.partnerId),
    enabled: !!props.partnerId && !props.containers,
  });
  const { data: fetchedStructuredColumns } = useQuery({
    ...partnerStructuredColumnsQueryOptions(props.partnerId),
    enabled: !!props.partnerId && !props.structuredColumns,
  });
  const containers = props.containers ?? fetchedContainers;
  const structuredColumns = props.structuredColumns ?? fetchedStructuredColumns;
  // The partner's previous-upload mapping (backend template) — the single
  // source of truth we pre-fill from. Auto (Drive) imports reuse the same.
  const { data: templateMapping = [] } = useQuery(latestMappingQueryOptions(props.partnerId));
  const createImport = useCreateImport();

  const catalog: ImportTarget[] = useMemo(
    () =>
      buildImportCatalog({
        containers: containers ?? [],
        structuredColumns: structuredColumns ?? [],
        metadataDefinitions,
      }),
    [containers, structuredColumns, metadataDefinitions],
  );

  const [step, setStep] = useState<Step>('select');
  const [files, setFiles] = useState<File[]>([]);
  const [parsed, setParsed] = useState<ParsedWorkbook | null>(null);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [mapping, setMapping] = useState<ImportMapping>([]);
  const [deselected, setDeselected] = useState<ReadonlySet<number>>(new Set());

  // Products back the Product / Product Code targets. Only fetched once the
  // mapping names one, so an ordinary container-mapped import pays nothing.
  const namesProduct = mapping.some(
    (m) => m.targetField === PRODUCT_NAME_TARGET || m.targetField === PRODUCT_CODE_TARGET,
  );
  const { data: productList = [] } = useQuery({
    ...productListQueryOptions(props.partnerId),
    enabled: !!props.partnerId && namesProduct,
  });
  const products = useMemo(() => toImportProducts(productList), [productList]);

  // Destructured so the validation memo depends on stable field identities
  // rather than the inline-built `project` object.
  const projectWorkOrders = project?.workOrders ?? EMPTY_WORK_ORDERS;
  const projectLegacyWorkOrderIds = project?.legacyWorkOrderIds ?? EMPTY_LEGACY_IDS;
  const projectStatus: ProjectStatus = project?.projectStatus ?? 'draft';

  function reset() {
    setStep('select');
    setFiles([]);
    setParsed(null);
    setParsing(false);
    setParseError(null);
    setMapping([]);
    setDeselected(new Set());
  }

  function handleOpenChange(next: boolean) {
    if (!next && createImport.isPending) return;
    if (!next) reset();
    onOpenChange(next);
  }

  async function parseFile(file: File, sheetIndex: number) {
    setParsing(true);
    setParseError(null);
    try {
      const workbook = await parseWorkbook(file, sheetIndex);
      setParsed(workbook);
      if (workbook.rows.length === 0) setParseError('No data rows were found in this sheet.');
    } catch {
      setParsed(null);
      setParseError('Could not read this file. Please upload a valid .xlsx, .xls or .csv file.');
    } finally {
      setParsing(false);
    }
  }

  function handleFilesChange(next: File[]) {
    setFiles(next);
    setParsed(null);
    setParseError(null);
    const file = next[0];
    if (file) void parseFile(file, 0);
  }

  /**
   * Seed the mapping from the partner's backend template (entries whose source
   * columns all exist in this file), then auto-match remaining headers to a
   * catalog target by label (a `whole` transform), keeping the prior behaviour.
   */
  function seedMapping(headers: string[]): ImportMapping {
    const headerSet = new Set(headers);
    const targetByField = new Map(catalog.map((t) => [t.field, t]));
    const labelToField = new Map(catalog.map((t) => [t.label.toLowerCase(), t.field]));
    const knownTarget = (field: string) =>
      targetByField.has(field) ||
      field === PROJECT_CODE_TARGET ||
      field === PROJECT_NAME_TARGET ||
      field === PRODUCT_CODE_TARGET ||
      field === PRODUCT_NAME_TARGET;

    const seeded: ImportMapping = [];
    const usedTargets = new Set<string>();
    const usedHeaders = new Set<string>();

    // 1. Carry over template entries whose referenced headers all exist here.
    for (const entry of templateMapping) {
      if (!knownTarget(entry.targetField) || usedTargets.has(entry.targetField)) continue;
      // `static` entries reference no source column, so they always carry over.
      const referenced =
        entry.transform.kind === 'concat'
          ? entry.transform.headers
          : 'header' in entry.transform
            ? [entry.transform.header]
            : [];
      if (!referenced.every((h) => headerSet.has(h))) continue;
      seeded.push(entry);
      usedTargets.add(entry.targetField);
      for (const h of referenced) usedHeaders.add(h);
    }

    // 2. Auto-match any remaining header to a catalog target by label.
    for (const header of headers) {
      if (usedHeaders.has(header)) continue;
      const field = labelToField.get(header.toLowerCase());
      if (!field || usedTargets.has(field)) continue;
      const target = targetByField.get(field);
      const unit =
        target?.kind === 'number_with_unit'
          ? (target.defaultUnit ?? target.units?.[0] ?? '')
          : undefined;
      seeded.push({
        targetField: field,
        transform: { kind: 'whole', header },
        ...(unit !== undefined ? { unit } : {}),
      });
      usedTargets.add(field);
      usedHeaders.add(header);
    }

    // Uploaded imports always create work orders as Draft, whichever surface
    // launched them — only Automatic (Drive) imports may create to Scheduled.
    // Importing from within a project additionally fixes the target project:
    // Project Code / Name become locked static values equal to this project's.
    // These override anything the template / auto-match produced.
    const forced: ImportMapping = [
      { targetField: WORK_ORDER_STATUS_TARGET, transform: { kind: 'static', value: 'draft' } },
      ...(project
        ? [
            {
              targetField: PROJECT_CODE_TARGET,
              transform: { kind: 'static' as const, value: project.projectCode },
            },
            {
              targetField: PROJECT_NAME_TARGET,
              transform: { kind: 'static' as const, value: project.projectName },
            },
          ]
        : []),
    ];
    const forcedFields = new Set(forced.map((f) => f.targetField));
    return [...forced, ...seeded.filter((e) => !forcedFields.has(e.targetField))];
  }

  // Without a pinned project the sheet must say which project each row belongs
  // to — the server matches an existing project by code or creates one, and a
  // row with no code has nothing to fall back to.
  const missingRequired = getMissingRequiredMappingFields(mapping, { hasProject: project != null });
  const canPreview = missingRequired.length === 0;

  const validation = useMemo(() => {
    if (!parsed) return null;
    return validateImport({
      rows: parsed.rows,
      mapping,
      catalog,
      // At partner level there is no single project to diff against, so every
      // row previews as new; the server resolves each row's real project.
      workOrders: projectWorkOrders,
      containers: containers ?? [],
      structuredColumns: structuredColumns ?? [],
      legacyWorkOrderIds: projectLegacyWorkOrderIds,
      projectStatus: projectStatus,
      products,
    });
    // Depend on the fields consumed, not on `project` itself: callers build that
    // object inline, so its identity changes every render and would re-run the
    // whole validation over every parsed row each time.
  }, [
    parsed,
    mapping,
    catalog,
    projectWorkOrders,
    projectLegacyWorkOrderIds,
    projectStatus,
    containers,
    structuredColumns,
    products,
  ]);

  async function handleImport() {
    const file = files[0];
    if (!file) return;
    try {
      const created = await createImport.mutateAsync({
        partnerId: props.partnerId,
        file,
        mapping,
        projectId: project?.projectId ?? null,
      });
      toast.success('Import queued');
      reset();
      onOpenChange(false);
      void navigate({ to: '/imports/$importId', params: { importId: created.import_id } });
    } catch (err) {
      toast.error(
        `Could not queue the import: ${err instanceof Error ? err.message : 'Unknown error'}`,
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Import from spreadsheet</DialogTitle>
          <DialogDescription>{stepDescription(step, project != null)}</DialogDescription>
        </DialogHeader>

        {step === 'select' && (
          <div className="space-y-3">
            <FileSelection
              files={files}
              onFilesChange={handleFilesChange}
              accept=".xlsx,.xls,.csv"
              multiple={false}
            />
            {parsing && <p className="text-muted-foreground text-sm">Reading file…</p>}
            {parseError && <p className="text-destructive text-sm">{parseError}</p>}
            {parsed && parsed.sheetNames.length > 1 && (
              <label className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Sheet</span>
                <select
                  className="border-input h-8 rounded-md border bg-transparent px-2 text-sm shadow-sm"
                  value={parsed.sheetName}
                  onChange={(e) => {
                    const idx = parsed.sheetNames.indexOf(e.target.value);
                    if (files[0] && idx >= 0) void parseFile(files[0], idx);
                  }}
                >
                  {parsed.sheetNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {parsed && parsed.rows.length > 0 && (
              <p className="text-muted-foreground text-sm">
                {parsed.rows.length} row{parsed.rows.length === 1 ? '' : 's'} ·{' '}
                {parsed.headers.length} column{parsed.headers.length === 1 ? '' : 's'} found.
              </p>
            )}
          </div>
        )}

        {step === 'map' && parsed && (
          <ImportMappingEditor
            headers={parsed.headers}
            catalog={catalog}
            value={mapping}
            onChange={setMapping}
            lockedFields={project ? LOCKED_PROJECT_IMPORT_FIELDS : LOCKED_PARTNER_IMPORT_FIELDS}
            {...(project ? {} : { requiredFields: PARTNER_IMPORT_REQUIRED_FIELDS })}
          />
        )}

        {step === 'preview' && validation && (
          <div className="space-y-2">
            <p className="text-muted-foreground text-sm">
              This preview is advisory. The file and mapping are uploaded and applied on the server;
              progress appears under Settings → Work Order Imports.
              {!project &&
                ' Rows are shown as new because each row’s project is resolved server-side — rows' +
                  ' matching a work order in an existing project will update it instead.'}
            </p>
            <PreviewStep
              previews={validation.previews}
              counts={validation.counts}
              deselected={deselected}
              onToggle={(sourceIndex) => {
                setDeselected((prev) => {
                  const next = new Set(prev);
                  if (next.has(sourceIndex)) next.delete(sourceIndex);
                  else next.add(sourceIndex);
                  return next;
                });
              }}
            />
          </div>
        )}

        <DialogFooter>
          {step === 'select' && (
            <Button
              disabled={!parsed || parsed.rows.length === 0 || parsing}
              onClick={() => {
                if (!parsed) return;
                setMapping(seedMapping(parsed.headers));
                setStep('map');
              }}
            >
              Continue
            </Button>
          )}

          {step === 'map' && (
            <>
              <Button variant="outline" onClick={() => setStep('select')}>
                Back
              </Button>
              <Button
                disabled={!canPreview}
                title={
                  canPreview
                    ? undefined
                    : `Map a column to ${missingRequired.join(' and ')} to continue`
                }
                onClick={() => {
                  setDeselected(new Set());
                  setStep('preview');
                }}
              >
                Preview
              </Button>
            </>
          )}

          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={() => setStep('map')}>
                Back
              </Button>
              <Button disabled={createImport.isPending} onClick={() => void handleImport()}>
                {createImport.isPending ? 'Queuing…' : 'Upload & import'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function stepDescription(step: Step, hasProject: boolean): string {
  switch (step) {
    case 'select':
      return hasProject
        ? 'Choose an Excel or CSV file to import into this project.'
        : 'Choose an Excel or CSV file. Each row names its own project, which is matched by code or created.';
    case 'map':
      return hasProject
        ? 'Match each planner column to a spreadsheet column (with an optional transform). Map the Work Order column to continue.'
        : 'Match each planner column to a spreadsheet column (with an optional transform). Map the Work Order and Project Code columns to continue.';
    case 'preview':
      return 'Review what will change. The whole file is processed on the server.';
  }
}
